import type { CSSProperties } from "react";
import dayjs, { ConfigType } from "dayjs";

import {
  FeatureFlagContext,
  FeatureFlagType,
} from "interfaces/feature-flags";

type Option<TValue> = {
  label: string;
  value: TValue;
};

export const FEATURE_FLAG_KEY_PATTERN = /^[a-z0-9_-]+$/;

export const FEATURE_FLAG_TYPE_OPTIONS: Option<FeatureFlagType>[] = [
  { label: "RELEASE", value: "release" },
  { label: "EXPERIMENT", value: "experiment" },
  { label: "OPS", value: "ops" },
  { label: "KILL SWITCH", value: "kill-switch" },
];

export const FEATURE_FLAG_CONTEXT_OPTIONS: Option<FeatureFlagContext>[] = [
  { label: "Frontend", value: "frontend" },
  { label: "Backend", value: "backend" },
  { label: "Ambos", value: "both" },
];

export const FEATURE_FLAG_STATUS_OPTIONS: Option<boolean>[] = [
  { label: "Ativo", value: true },
  { label: "Inativo", value: false },
];

export const FEATURE_FLAG_TYPE_TAG_STYLE_MAP: Record<
  FeatureFlagType,
  CSSProperties
> = {
  release: {
    backgroundColor: "#E5EAF6",
    color: "#0435AC",
  },
  experiment: {
    backgroundColor: "#F5E5F6",
    color: "#9E04AC",
  },
  ops: {
    backgroundColor: "#FFF9F2",
    color: "#FF8800",
  },
  "kill-switch": {
    backgroundColor: "#FFF6F2",
    color: "#FF4E05",
  },
};

export const isValidFeatureFlagKey = (key: string): boolean =>
  FEATURE_FLAG_KEY_PATTERN.test(key);

export const getFeatureFlagTypeLabel = (type: FeatureFlagType): string => {
  const option = FEATURE_FLAG_TYPE_OPTIONS.find((item) => item.value === type);
  return option ? option.label : type;
};

export const getFeatureFlagContextLabel = (
  context: FeatureFlagContext,
): string => {
  const option = FEATURE_FLAG_CONTEXT_OPTIONS.find(
    (item) => item.value === context,
  );
  return option ? option.label : context;
};

export const getFeatureFlagStatusLabel = (enabled: boolean): string => {
  const option = FEATURE_FLAG_STATUS_OPTIONS.find(
    (item) => item.value === enabled,
  );
  return option ? option.label : "";
};

export const getFeatureFlagExpirationInfo = (
  expiresAt?: ConfigType | null,
  referenceDate: ConfigType = dayjs(),
): { text: string; color: string } | null => {
  if (!expiresAt) {
    return null;
  }

  const daysUntilExpiration = dayjs(expiresAt)
    .startOf("day")
    .diff(dayjs(referenceDate).startOf("day"), "day");

  if (daysUntilExpiration < 0) {
    return { text: "Expirado", color: "#FF4E05" };
  }

  const text = `Em ${daysUntilExpiration} ${
    daysUntilExpiration === 1 ? "dia" : "dias"
  }`;

  if (daysUntilExpiration <= 15) {
    return { text, color: "#FF4E05" };
  }

  if (daysUntilExpiration < 30) {
    return { text, color: "#FFB020" };
  }

  return { text, color: "#3F8E12" };
};
